import { useEffect, useMemo, useRef } from 'react';
import type { EventsPage } from '@finder/contracts';
import { FilteredEmptyState } from '../states/States.js';
import { EventCard } from './EventCard.js';
import { ImpressionTracker, collectFirstRenderTokens } from './impression-tracker.js';
import {
  applyListView,
  describeResults,
  DEFAULT_LIST_FILTERS,
  type ListFilters,
} from './list-view.js';
import { RESULTS_HEADING_ID } from './results-focus.js';

export type { ListFilters } from './list-view.js';

function weekendCleared(filters: ListFilters): boolean {
  return (
    filters.when === 'weekend' &&
    filters.offer === 'either' &&
    !filters.hideRestricted &&
    !filters.confirmedOnly &&
    !filters.happeningNow
  );
}

/**
 * Ordered results list. The default sort is the server ranking (`best`).
 * Soonest and nearest reorder only this rendered list; they do not re-score
 * and they do not change the map/list projections. On first render of each
 * card for a response it fires a best-effort batched impression (at most once
 * per response/card) without blocking render. Impressions are collected for
 * the cards that are actually rendered after filtering.
 */
export function EventList({
  page,
  filters = DEFAULT_LIST_FILTERS,
  onImpressions,
  onOpenDetail,
  onClearClientFilters,
  selectedId = null,
  distanceVoice = 'place',
}: {
  page: EventsPage;
  filters?: ListFilters;
  onImpressions: (tokens: string[]) => void;
  /** Opens the in-app detail view for one event. Omitted, the card has no detail button. */
  onOpenDetail?: (canonicalId: string) => void;
  selectedId?: string | null;
  distanceVoice?: 'gps' | 'place';
  /**
   * Clears offer, happening-now, confirmed-food, and hide-restricted.
   * Shown only when those filters remove every row the API returned.
   */
  onClearClientFilters?: () => void;
}): JSX.Element {
  const trackerRef = useRef(new ImpressionTracker());

  const visible = useMemo(() => applyListView(page.events, filters), [page.events, filters]);
  const hasUnconfirmed = visible.some((event) => event.foodState !== 'confirmed');

  useEffect(() => {
    // A new responseId scopes a fresh set of cards.
    trackerRef.current.reset();
    const tokens = collectFirstRenderTokens(
      page.responseId,
      visible.map((event) => ({
        canonicalId: event.canonicalId,
        impressionToken: event.impressionToken,
      })),
      trackerRef.current,
    );
    onImpressions(tokens);
  }, [page.responseId, visible, onImpressions]);

  return (
    <>
      <h2 id={RESULTS_HEADING_ID} tabIndex={-1} className="results__count">
        {describeResults(visible.length, filters)}
      </h2>
      {hasUnconfirmed ? (
        <p className="results__unconfirmed">
          Unconfirmed means the listing mentions free food, and we have not checked it.
        </p>
      ) : null}
      {visible.length === 0 ? (
        weekendCleared(filters) ? (
          <div className="state-card state-card--empty" role="status">
            <h3>Nothing with free food this weekend.</h3>
          </div>
        ) : (
          <FilteredEmptyState
            {...(onClearClientFilters ? { onClear: onClearClientFilters } : {})}
          />
        )
      ) : (
        <ol className="event-list" aria-label="events near you">
          {visible.map((event) => (
            <li key={event.canonicalId}>
              {onOpenDetail ? (
                <EventCard
                  event={event}
                  onOpenDetail={onOpenDetail}
                  selected={selectedId === event.canonicalId}
                  distanceVoice={distanceVoice}
                />
              ) : (
                <EventCard
                  event={event}
                  selected={selectedId === event.canonicalId}
                  distanceVoice={distanceVoice}
                />
              )}
            </li>
          ))}
        </ol>
      )}
    </>
  );
}
